import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { CarPlate } from "../../http/carPlates/types";
import { useAdminCarsApi } from "../../http/cars/api";
import { Car, CarNoCarDepency } from "../../http/cars/types";
import { Manufacturer } from "../../http/manufacturers/types";
import { formatDateNumeric, getDefaultDateForm, isOlderThanAWeek } from "../../types/utils";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import reactSelectStyles from "../ui/ReactSelectUtils";

const CAR_FIELDS = ["id", "vehicleIdentificationNumber", "currentPlateNumber", "lastPlateNumber"] as const;
type CarField = (typeof CAR_FIELDS)[number];
type CarSearch = Pick<CarNoCarDepency, CarField>;

export interface CarPlateFormBody {
  id: string;
  car: {
    id: string;
    vehicleIdentificationNumber: string;
    manufacturer: Manufacturer;
    model: string;
    registrationDate: string;
  };
  startDate: string;
  endDate?: string;
  nr: string;
  prevEndDate?: string;
}

type CarPlateFormProps = {
  carPlate?: CarPlate | null;
  onSubmit: (form: CarPlateFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  allowSelectCar?: boolean;
  isCarOverview?: boolean;
};

const CarPlateForm: FC<CarPlateFormProps> = ({
  carPlate,
  onSubmit,
  isDisabled,
  isLoading,
  allowSelectCar,
  isCarOverview,
}: CarPlateFormProps) => {
  const i18n = useI18n();
  const carsApi = useAdminCarsApi();

  const [defaultCarOptions, setDefaultCarOptions] = useState<CarSearch[]>([]);

  const defaultDate = getDefaultDateForm(carPlate?.startDate, carPlate?.endDate);

  const defaultValues = {
    car: carPlate?.car,
    startDate: !!isCarOverview ? defaultDate : carPlate?.startDate,
    endDate: !isCarOverview ? carPlate?.endDate : undefined,
    nr: !isCarOverview ? carPlate?.nr : undefined,
    prevEndDate: (!!isCarOverview && carPlate?.endDate) || defaultDate,
  };

  return (
    <Form<CarPlateFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
      itemCreationInfo={carPlate}
    >
      {({ register, formState: { errors }, control, reset, watch, setValue }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [carPlate]);

        return (
          <FormFieldsContainer>
            {isCarOverview && !!carPlate && (
              <>
                <FormField label={i18n.t.common.currentPlateNumber}>
                  <Input disabled value={carPlate.nr} />
                </FormField>
                <FormField label={i18n.t.common.from}>
                  <Input disabled value={formatDateNumeric(carPlate.startDate, i18n.language)} />
                </FormField>
                <Controller
                  name="prevEndDate"
                  control={control}
                  rules={{
                    required: true,
                    validate: (value) => {
                      if (value) {
                        if (value === carPlate.startDate) {
                          return i18n.t.common.endDateCantMatchStartDate;
                        }
                        if (value < carPlate.startDate) {
                          return i18n.t.common.endDateBeforeStartDate;
                        }
                      }
                      return undefined;
                    },
                  }}
                  render={({ fieldState }) => (
                    <FormField label={i18n.t.common.to} error={fieldState.error}>
                      {({ isInvalid }) => (
                        <DatePicker
                          onChange={(v) => setValue("prevEndDate", v!)}
                          isInvalid={isInvalid}
                          value={watch("prevEndDate")}
                          disabled={isDisabled || isLoading || !!carPlate.endDate}
                        />
                      )}
                    </FormField>
                  )}
                />
              </>
            )}
            {!!allowSelectCar && (
              <Controller
                name="car"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.cars.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<CarSearch, CarField>(aborter, searchTerm, carsApi.getCars, {
                        fields: CAR_FIELDS,
                      });

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultCarOptions);
                      }, []);

                      return (
                        <AsyncSelect
                          menuPosition="fixed"
                          {...field}
                          inputId={labelId}
                          defaultOptions={defaultCarOptions}
                          loadOptions={loadOptions}
                          isLoading={isLoading}
                          isDisabled={!!carPlate || isDisabled || isLoading}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) =>
                            x.currentPlateNumber ||
                            x.lastPlateNumber ||
                            x.vehicleIdentificationNumber ||
                            `${x.manufacturer?.name} ${x.model} ${
                              x.registrationDate && formatDateNumeric(x.registrationDate, i18n.language)
                            }`
                          }
                          getOptionValue={(x) => x.id ?? ""}
                          styles={reactSelectStyles<Partial<Car>>(isInvalid)}
                        />
                      );
                    }}
                  </FormField>
                )}
              />
            )}
            <FormField label={isCarOverview ? i18n.t.plateNumbers.singular : i18n.t.common.number} error={errors.nr}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={
                    isDisabled ||
                    isLoading ||
                    (!!carPlate?.id && !isCarOverview && isOlderThanAWeek(carPlate.createdAt))
                  }
                  {...register("nr", {
                    minLength: 1,
                    required: true,
                  })}
                />
              )}
            </FormField>
            <Controller
              name="startDate"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  if (!!isCarOverview) {
                    if (value) {
                      const prevEndDate = watch("prevEndDate");
                      if (carPlate) {
                        if (prevEndDate) {
                          if (value >= carPlate.startDate && value < prevEndDate) {
                            return i18n.t.common.startDateFallsInCurrentAssignmentPeriod;
                          }
                        }
                      }
                    }
                  }
                  return undefined;
                },
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.startDate} error={fieldState.error}>
                  {({ isInvalid }) => (
                    <DatePicker
                      onChange={(v) => !!v && setValue("startDate", v)}
                      isInvalid={isInvalid}
                      value={watch("startDate")}
                      disabled={isDisabled || isLoading}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="endDate"
              control={control}
              rules={{
                validate: (value) => {
                  if (value) {
                    const startDate = watch("startDate");
                    if (value === startDate) {
                      return i18n.t.common.endDateCantMatchStartDate;
                    }
                    if (value < startDate) {
                      return i18n.t.common.endDateBeforeStartDate;
                    }
                  }
                  return undefined;
                },
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.endDate} error={fieldState.error} isOptional>
                  {({ isInvalid }) => (
                    <DatePicker
                      onChange={(v) => setValue("endDate", v!)}
                      isInvalid={isInvalid}
                      value={watch("endDate")}
                      disabled={isDisabled || isLoading}
                      clearable
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default CarPlateForm;
